import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import api from "@/api/axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2, PlusCircle } from "lucide-react";

// ✅ Schema for validation
const projectSchema = z.object({
    code: z.string().min(1, "Project code is required."),
    name: z.string().min(1, "Project name is required."),
    total_fee: z
        .coerce.number()
        .min(0, "Total fee must be a positive number.")
        .default(0),
});

export function AddProjectDialog({ onProjectAdded }) {
    const [open, setOpen] = useState(false);

    const form = useForm({
        resolver: zodResolver(projectSchema),
        defaultValues: { code: "", name: "", total_fee: 2500000 },
    });

    const { isSubmitting } = form.formState;

    const onSubmit = async (values) => {
        try {
            await api.post(`${API}/projects`, values);
            toast.success("✅ Project created successfully!");
            onProjectAdded?.();
            setOpen(false);
            form.reset();
        } catch (error) {
            toast.error(error.response?.data?.detail || "❌ Failed to create project.");
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="gap-2">
                    <PlusCircle className="h-4 w-4" />
                    Add Project
                </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-md rounded-2xl shadow-lg">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold">
                        Add New Project
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        Fill in the details for your project and click save when you’re done.
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-6"
                    >
                        {/* Project Code */}
                        <FormField
                            control={form.control}
                            name="code"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Project Code</FormLabel>
                                    <FormControl>
                                        <Input placeholder="e.g., P2023-01" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Project Name */}
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Project Name</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="e.g., Corporate Website Redesign"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Total Fee */}
                        <FormField
                            control={form.control}
                            name="total_fee"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Total Fee (₹)</FormLabel>
                                    <FormControl>
                                        <Input type="number" placeholder="0" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Actions */}
                        <DialogFooter className="pt-2">
                            <Button
                                type="submit"
                                className="w-full sm:w-auto"
                                disabled={isSubmitting}
                            >
                                {isSubmitting && (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Save Project
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
